import * as React from 'react'
import {
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
} from '@mui/x-data-grid-pro'
import { Box } from '@mui/material'
import { addDays, format, isAfter, isBefore, isValid } from 'date-fns'
import {
  QueryDslQueryContainer,
  OperatorValueDate,
  handleDateOsFilter,
  handleDateTimeOsFilter,
} from '../utils'
import { DatePicker } from '../../base'

export function DateBetweenFilter(props: GridFilterInputValueProps) {
  const { item, applyValue, focusElementRef } = props
  const parseDate = (val?: any) => {
    if (val) {
      const date = new Date(val)
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
      return date
    }

    return null
  }

  const [startDate, setStartDate] = React.useState<Date | null>(
    parseDate(item.value?.start),
  )
  const [endDate, setEndDate] = React.useState<Date | null>(
    parseDate(item.value?.end),
  )

  const handleChange = React.useMemo(
    () => (newStart: Date | null, newEnd: Date | null) => {
      if (newStart && !isValid(newStart)) return
      if (newEnd && !isValid(newEnd)) return

      applyValue({
        ...item,
        value: {
          start: newStart ? format(newStart, 'yyyy-MM-dd') : null,
          end: newEnd ? format(newEnd, 'yyyy-MM-dd') : null,
        },
      })
    },
    [applyValue, item],
  )

  return (
    <Box display="flex" gap={1} pr={1}>
      <DatePicker
        label="Start Date"
        value={startDate}
        onChange={(date) => {
          setStartDate(date)
          handleChange(date, endDate)
        }}
        maxDate={endDate || undefined}
        slotProps={{
          textField: { variant: 'standard', inputRef: focusElementRef },
        }}
        sx={{ maxWidth: '150px' }}
      />
      <DatePicker
        label="End Date"
        value={endDate}
        onChange={(date) => {
          setEndDate(date)
          handleChange(startDate, date)
        }}
        minDate={startDate || undefined}
        slotProps={{
          textField: { variant: 'standard' },
        }}
        sx={{ maxWidth: '150px' }}
      />
    </Box>
  )
}

export function getGridDateBetweenOperator(): GridFilterOperator {
  return {
    label: 'between',
    value: OperatorValueDate.between,
    getApplyFilterFn: (filterItem) => {
      if (
        !filterItem.value ||
        !filterItem.value.start ||
        !filterItem.value.end
      ) {
        return null
      }
      const start = new Date(filterItem.value.start)
      const end = new Date(filterItem.value.end)

      if (!isValid(start) || !isValid(end)) {
        return null
      }

      return ({ value }) => {
        if (!value) return false
        const cellDate = new Date(value)
        return (
          isAfter(cellDate, addDays(start, -1)) &&
          isBefore(cellDate, addDays(end, 1))
        )
      }
    },
    InputComponent: DateBetweenFilter,
  }
}

export function filterItemToOSParamForDateBetween(
  field: string,
  params: {
    nestedPath?: string,
    innerHits?: boolean,
    additionalQuery?: QueryDslQueryContainer[],
    isDateTime?: boolean
  } = {}
) {
  return (item: GridFilterItem): QueryDslQueryContainer | undefined => {
    const { nestedPath, innerHits, additionalQuery, isDateTime } = params

    if (!item.value || !item.value.start) return undefined
    const itemAfter: GridFilterItem = {
      field,
      operator: OperatorValueDate.onOrAfter,
      value: item.value.start as string,
    }

    if (!item.value.end) return undefined

    const itemBefore: GridFilterItem = {
      field,
      operator: OperatorValueDate.onOrBefore,
      value: item.value.end as string,
    }
    const op1 = isDateTime ? handleDateTimeOsFilter(itemAfter, field) : handleDateOsFilter(itemAfter, field)
    const op2 = isDateTime ? handleDateTimeOsFilter(itemBefore, field) : handleDateOsFilter(itemBefore, field)
    if (!op1 || !op2) return undefined
    if (!nestedPath) {
      return {
        bool: {
          must: [op1, op2, ...(additionalQuery || [])],
        },
      }
    }
    return {
      nested: {
        inner_hits: innerHits ? {} : undefined,
        path: nestedPath,
        query: {
          bool: {
            must: [op1, op2, ...(additionalQuery || [])],
          },
        },
      },
    }
  }
}
